import React, { useEffect, useState, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { IoAddSharp } from 'react-icons/io5';
import '../pagination/pagination.css';
import Pagination from '../pagination/pagination';
import LeaseApiService from '../../services/apiService';
import UnitReleaseRAApi from './unitReleaseRAApi';
import { extractDropdownArray, toOption, getReleaseTypeBadgeStyle, getStatusBadgeStyle, formatDateForApi } from './unitReleaseRAHelpers';

const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: state.isFocused ? '#000 ' : '#000',
    ':hover': { backgroundColor: '#F5F5F5', color: '#000 ' },
  }),
};

const UNIT_CLASS_OPTIONS = [
  { value: 'Lease', label: 'Lease' },
  { value: 'PM', label: 'PM' },
  { value: 'Sale', label: 'Sale' },
];

const emptyValueFormatter = (v) => (v === null || v === undefined || v === '' ? '--' : v);

const Badge = ({ text, style }) =>
  text ? (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {text}
    </span>
  ) : (
    '--'
  );

const defaultDateRange = () => ({
  fromDate: moment().subtract(30, 'days').toDate(),
  toDate: new Date(),
});

const UnitReleaseRAList = () => {
  const api = new UnitReleaseRAApi();
  const apiService = new LeaseApiService();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [releaseTypeOptions, setReleaseTypeOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [pagination, setPagination] = useState();
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize')) || 50);
  const [FilterTab, setFilterTab] = useState(true);

  const { fromDate: defaultFrom, toDate: defaultTo } = defaultDateRange();
  const initialUnitNo = searchParams.get('unitNo');
  const [formData, setFormData] = useState({
    property: null,
    unitNo: initialUnitNo ? { value: initialUnitNo, label: initialUnitNo } : null,
    resvNo: searchParams.get('resvNo') || '',
    releaseType: null,
    unitClass: null,
    releasedBy: searchParams.get('releasedBy') || '',
    fromDate: searchParams.get('fromDate') ? moment(searchParams.get('fromDate')).toDate() : defaultFrom,
    toDate: searchParams.get('toDate') ? moment(searchParams.get('toDate')).toDate() : defaultTo,
  });

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const res = await api.getDropdowns(['Properties', 'ReleaseTypes']);
        const dropdowns = res?.data?.dropdowns || res?.data?.result?.dropdowns || {};
        setPropertyOptions(extractDropdownArray(dropdowns, 'Properties').map(toOption));
        setReleaseTypeOptions(extractDropdownArray(dropdowns, 'ReleaseTypes').map(toOption));
      } catch (error) {
        console.error('[UnitReleaseRA] Error loading dropdowns:', error);
      }
    };
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same property -> unit cascade as the Unit Release (Unit Block) listing.
  const fetchUnitOptions = async (propertyValue) => {
    if (!propertyValue) {
      setUnitOptions([]);
      return;
    }
    setLoadingUnits(true);
    try {
      const params = new URLSearchParams();
      params.append('DropdownFor', 'CustUnitNoByPropertyNo');
      params.append('CascadValue', propertyValue);
      const res = await apiService.getApiParamater('CUSTOMER_CASCAD', `?${params.toString()}`);
      setUnitOptions(res?.data?.items || []);
    } catch (error) {
      console.error('[UnitReleaseRA] Error loading unit dropdown:', error);
      setUnitOptions([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handlePropertyChange = (opt) => {
    setFormData((prev) => ({ ...prev, property: opt, unitNo: null }));
    fetchUnitOptions(opt?.value);
  };

  const buildFilters = useCallback(
    (data = formData) => ({
      propertyNo: data.property?.value?.value ?? data.property?.value ?? '',
      unitNo: data.unitNo?.value || '',
      resvNo: data.resvNo || '',
      releaseType: data.releaseType?.label || '',
      unitClass: data.unitClass?.value || '',
      releasedBy: data.releasedBy || '',
      fromDate: formatDateForApi(data.fromDate),
      toDate: formatDateForApi(data.toDate),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData],
  );

  const syncSearchParams = (filters, pageNo) => {
    const next = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) next[key] = value;
    });
    if (pageNo && pageNo !== 1) next.pageNo = pageNo;
    if (pageSize && pageSize !== 50) next.pageSize = pageSize;
    setSearchParams(next, { replace: true });
  };

  const getItems = async (pageNo = 1, formOverride) => {
    setLoading(true);
    try {
      const filters = buildFilters(formOverride || formData);
      syncSearchParams(filters, pageNo);
      const res = await api.getReleases(filters, pageNo, pageSize);
      const rows = res?.data?.result || [];
      setRowData(rows);
      setPagination(
        res?.data?.paginationData || {
          currentPage: pageNo,
          pageSize,
          totalRecords: rows[0]?.totalRecords ?? rows.length,
        },
      );
    } catch (error) {
      // A 204 (no matching rows) resolves normally (axios treats it as 2xx) and
      // is handled above via the `|| []` fallback on an empty response body -
      // this branch is only real request failures.
      console.error('[UnitReleaseRA] Error fetching releases:', error);
      setRowData([]);
      setPagination(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getItems(Number(searchParams.get('pageNo')) || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearFilters = () => {
    const { fromDate, toDate } = defaultDateRange();
    const blank = { property: null, unitNo: null, resvNo: '', releaseType: null, unitClass: null, releasedBy: '', fromDate, toDate };
    setFormData(blank);
    setUnitOptions([]);
    // Pass the reset filters directly rather than relying on `formData` state,
    // which wouldn't have re-rendered yet (setTimeout doesn't wait for React -
    // it would still close over the pre-reset values).
    getItems(1, blank);
  };

  const columnDefs = [
    { headerName: 'Date', field: 'releaseDate', suppressMovable: true, width: 130, valueFormatter: (p) => (p.value ? moment(p.value).format('DD-MMM-YYYY') : '--') },
    {
      headerName: 'Release Type',
      field: 'releaseType',
      suppressMovable: true,
      width: 170,
      cellRenderer: (p) => <Badge text={p.value} style={getReleaseTypeBadgeStyle(p.value)} />,
    },
    { headerName: 'Property', field: 'propertyName', suppressMovable: true, width: 180, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Unit No', field: 'unitNo', suppressMovable: true, width: 110, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Unit Ref No', field: 'unitRefNo', suppressMovable: true, width: 140, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Unit Class', field: 'unitClass', suppressMovable: true, width: 110, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Resv / Lease No', field: 'resvNo', suppressMovable: true, width: 150, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Side', field: 'releaseSide', suppressMovable: true, width: 100, valueFormatter: (p) => emptyValueFormatter(p.value) },
    {
      headerName: 'Current Status',
      field: 'currentStatus',
      suppressMovable: true,
      width: 150,
      cellRenderer: (p) => <Badge text={p.value} style={getStatusBadgeStyle(p.value)} />,
    },
    { headerName: 'Remarks', field: 'remarks', suppressMovable: true, flex: 1, minWidth: 180, valueFormatter: (p) => emptyValueFormatter(p.value) },
    { headerName: 'Released By', field: 'releasedBy', suppressMovable: true, width: 150, valueFormatter: (p) => emptyValueFormatter(p.value) },
  ];

  return (
    <div className="p-3">
      <div className="cost-center-section">
        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
          <div className="h6 pt-1">Search Parameters</div>
        </div>
        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
          <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
            <div className="form-group grid-contain">
              <div className="d-flex">
                <label className="form-label pt-2 start">Property</label>
                <Select
                  className="p-0 w-100"
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={formData.property}
                  isClearable
                  placeholder="Select Property"
                  onChange={handlePropertyChange}
                  options={propertyOptions}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">Unit No</label>
                <Select
                  className="p-0 w-100"
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={formData.unitNo}
                  isDisabled={loadingUnits || !formData.property}
                  isLoading={loadingUnits}
                  isClearable
                  placeholder="Select Unit"
                  onChange={(opt) => setFormData((prev) => ({ ...prev, unitNo: opt }))}
                  options={unitOptions?.map((item) => ({ value: item?.value, label: item?.text }))}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">Resv / Lease No</label>
                <input
                  type="text"
                  className="p-2 form-control w-75"
                  value={formData.resvNo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, resvNo: e.target.value }))}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">Release Type</label>
                <Select
                  className="p-0 w-100"
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={formData.releaseType}
                  isClearable
                  onChange={(opt) => setFormData((prev) => ({ ...prev, releaseType: opt }))}
                  options={releaseTypeOptions}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">Unit Class</label>
                <Select
                  className="p-0 w-100"
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={formData.unitClass}
                  isClearable
                  onChange={(opt) => setFormData((prev) => ({ ...prev, unitClass: opt }))}
                  options={UNIT_CLASS_OPTIONS}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">Released By</label>
                <input
                  type="text"
                  className="p-2 form-control w-75"
                  value={formData.releasedBy}
                  onChange={(e) => setFormData((prev) => ({ ...prev, releasedBy: e.target.value }))}
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">From Date</label>
                <DatePicker
                  selected={formData.fromDate}
                  onChange={(date) => setFormData((prev) => ({ ...prev, fromDate: date }))}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">To Date</label>
                <DatePicker
                  selected={formData.toDate}
                  onChange={(date) => setFormData((prev) => ({ ...prev, toDate: date }))}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>
              <div className="d-flex gap-1">
                <div onClick={() => getItems(1)} className="mainbtnGry flex-end gap-2 w-50">
                  <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Search</div>
                </div>
                <div onClick={handleClearFilters} className="mainbtnRed flex-end gap-2 w-50">
                  <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                  <div className="center">Reset</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-box2 p-3">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="d-flex justify-contant-between">
              <h5 className="col mainInvHead">Unit Release</h5>
              <div className="d-flex justify-content-end gap-1">
                <div className="col center">
                  <button className="mainbtn" type="button" onClick={() => navigate('/Customer/Property/Master/UnitReleaseRA/Create')}>
                    <IoAddSharp color="#fff" className="center mt-1" /> <div>New Release</div>
                  </button>
                </div>
              </div>
            </div>
            <div className="gridshad mt-2">
              {rowData.length === 0 ? (
                <div className="text-center p-5 text-muted">No releases found for the selected filters.</div>
              ) : (
                <div className="ag-theme-quartz" style={{ height: 650 }}>
                  <AgGridReact
                    getRowStyle={(params) => ({ background: params.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={(params) => String(params.data.releaseId)}
                  />
                </div>
              )}
              <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UnitReleaseRAList;
